import { useEffect, useState } from "react";
import { endpoints, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link2, Eye, RefreshCw, Copy } from "lucide-react";

const inputCls = "bg-white border-zinc-200 rounded-sm h-9 font-mono text-[12px] focus-visible:ring-orange-500/40";
const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast("Copied to clipboard"));

export default function SsoSettings() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [cfg, setCfg] = useState(null);
  const [secret, setSecret] = useState(null);

  const load = () => endpoints.ssoConfig().then(setCfg);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { setCfg(await endpoints.saveSsoConfig({ issuer: cfg.issuer, default_role: cfg.default_role })); toast.success("SSO settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const rotate = async () => {
    try { await endpoints.rotateSsoSecret(); toast.success("New shared secret generated", { description: "Reveal it once and store it in App1." }); setSecret(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reveal = async () => {
    try { const r = await endpoints.revealSsoSecret(); setSecret(r.secret); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!cfg) return null;
  const origin = API.replace(/\/api$/, "");
  return (
    <div className="border border-zinc-200 p-6 bg-white" data-testid="sso-section">
      <div className="flex items-center gap-2 mb-1"><Link2 className="w-4 h-4 text-orange-600" /><span className="tx-overline">App1 → App2 single sign-on</span></div>
      <p className="text-[12px] text-zinc-500 mb-4">
        A user signed into App1 (deal-amplify) opens App2 without a second login. App1 signs a short-lived HS256 JWT with the shared secret and redirects to
        <span className="font-mono text-zinc-800"> {origin}/sso?token=…</span>. Users are created in this workspace on first arrival.
      </p>
      <div className="grid grid-cols-[minmax(0,1fr)_160px_auto] gap-2 items-end mb-4">
        <div><div className="tx-overline mb-1.5">Issuer (iss claim)</div><Input data-testid="sso-issuer-input" value={cfg.issuer} disabled={!isOwner} onChange={(e) => setCfg({ ...cfg, issuer: e.target.value })} className={inputCls} /></div>
        <div><div className="tx-overline mb-1.5">Role for new users</div>
          <select data-testid="sso-role-select" value={cfg.default_role} disabled={!isOwner} onChange={(e) => setCfg({ ...cfg, default_role: e.target.value })} className="w-full h-9 bg-white border border-zinc-200 rounded-sm px-2 text-[12px] font-mono text-zinc-800">
            <option value="operator">operator</option><option value="owner">owner</option>
          </select></div>
        {isOwner && <Button onClick={save} data-testid="sso-save-button" className="bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm h-9">Save</Button>}
      </div>
      <div className="border border-zinc-200 bg-zinc-50 p-3 flex items-center gap-3 flex-wrap">
        <span className="tx-overline shrink-0">Shared secret</span>
        <span className={`font-mono text-[11px] ${cfg.enabled ? "text-emerald-700" : "text-amber-700"}`} data-testid="sso-status">
          {cfg.enabled ? `configured · rotated ${new Date(cfg.rotated_at).toLocaleString()}` : "not configured — SSO disabled"}
        </span>
        <div className="ml-auto flex gap-2">
          {isOwner && cfg.pending_reveal && (
            <button onClick={reveal} data-testid="sso-reveal-button" className="h-7 px-2.5 border border-orange-500/60 text-orange-700 bg-orange-50 hover:bg-orange-100 text-[11px] font-mono flex items-center gap-1.5"><Eye className="w-3 h-3" /> Reveal once</button>
          )}
          {isOwner && (
            <button onClick={rotate} data-testid="sso-rotate-button" className="h-7 px-2.5 border border-zinc-300 hover:bg-zinc-100 text-[11px] font-mono flex items-center gap-1.5"><RefreshCw className="w-3 h-3" /> {cfg.enabled ? "Rotate" : "Generate"}</button>
          )}
        </div>
      </div>
      {secret && (
        <div data-testid="sso-fresh-secret" className="border border-orange-500/40 bg-orange-50 p-3 mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="tx-overline text-orange-600">Copy now — this secret will not be shown again</span>
            <button onClick={() => setSecret(null)} data-testid="sso-dismiss-secret" className="text-[10px] font-mono text-zinc-500 hover:text-zinc-900">dismiss</button>
          </div>
          <div className="flex items-center gap-2"><code className="font-mono text-[12px] text-zinc-900 break-all flex-1" data-testid="sso-secret-value">{secret}</code><button onClick={() => copy(secret)} className="text-zinc-500 hover:text-zinc-900"><Copy className="w-3.5 h-3.5" /></button></div>
        </div>
      )}
      <pre className="mt-4 font-mono text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 p-3 overflow-x-auto leading-relaxed">{`// App1 (Node): sign & redirect
const token = jwt.sign({ iss: "${cfg.issuer}", aud: "transactr-cee", ws: "${cfg.workspace_id}",
  sub: user.id, email: user.email, name: user.name, jti: crypto.randomUUID() },
  process.env.CEE_SSO_SECRET, { algorithm: "HS256", expiresIn: "2m" });
res.redirect("${origin}/sso?token=" + token + "&next=/handoff");
// tokens are single-use (jti), max age ${cfg.max_age_seconds}s`}</pre>
    </div>
  );
}
